<!-- app/javascript/dashboard/routes/dashboard/bulkmessage/components/RecipientList.vue -->

<script setup>
import { ref, computed, onMounted, watch, reactive, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
// import Papa from 'papaparse';  // Currently unused - may be needed for future direct CSV import
// import * as XLSX from 'xlsx';   // Currently unused - may be needed for future direct Excel import

import Button from 'dashboard/components-next/button/Button.vue';
import AddContactsModal from './AddContactsModal.vue';
import RecipientsListHeader from './RecipientsListHeader.vue';
import FullTextModal from './modals/FullTextModal.vue';
import CsvMappingModal from './modals/CsvMappingModal.vue';
import CsvProcessingProgress from './CsvProcessingProgress.vue';
import FileValidationModal from './modals/FileValidationModal.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import ContactsPagination from './ContactsPagination.vue';

// Import composables
import { useRandomInbox } from '../utils/randomInboxUtils';
import usePhoneValidation from '../utils/usePhoneValidation';
import useRecipients from '../utils/useRecipients';
import useCsvImport from '../utils/useCsvImport';
import useFileValidation from '../utils/useFileValidation';
import useTextDisplay from '../utils/useTextDisplay';

const props = defineProps({
  recipients: {
    type: Array,
    required: true,
  },
  inboxes: {
    type: Array,
    required: true,
  },
  selectedInboxes: {
    type: Array,
    default: () => [],
  },
  selectedInboxId: {
    type: [Number, String],
    default: null,
  },
  availableLabels: {
    type: Array,
    default: () => [],
  },
  chatwootContacts: {
    type: Array,
    default: () => [],
  },
  isLoadingContacts: {
    type: Boolean,
    default: false,
  },
  totalContactsCount: {
    type: Number,
    default: 0,
    validator: value => {
      // Ensure it's a non-negative number
      return typeof value === 'number' && value >= 0;
    },
  },
  currentContactsPage: {
    type: Number,
    default: 1,
    validator: value => {
      // Ensure it's a positive number
      return typeof value === 'number' && value >= 1;
    },
  },
  totalContactsPages: {
    type: Number,
    default: 1,
    validator: value => {
      // Ensure it's a positive number
      return typeof value === 'number' && value >= 1;
    },
  },
  selectedIds: {
    type: Array,
    default: () => [],
  },
  selectedDDI: {
    type: String,
    default: '+55',
  },
  validationErrors: {
    type: Set,
    default: () => new Set(),
  },
  additionalColumns: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'import-csv',
  'add-from-label',
  'remove',
  'update-inbox',
  'update-multiple-inboxes',
  'update-random-inboxes',
  'update-name',
  'update-phone',
  'update-email',
  'delete-selected',
  'load-contacts',
  'update:selectedIds',
  'update:selectedDDI',
  'load-contacts-page',
  'update-custom-field',
  'inbox-dropdown-toggle',
  'set-all-recipients-inbox', // New simplified event for setting all recipients' inbox
]);

const { t } = useI18n();
const fileInput = ref(null);

// Editing states for inline editing
const editingStates = reactive({});
const originalValues = reactive({});

const startEditing = (recipientId, field) => {
  if (!editingStates[recipientId]) {
    editingStates[recipientId] = {};
  }
  if (!originalValues[recipientId]) {
    originalValues[recipientId] = {};
  }
  
  // Store original value before editing
  const recipient = props.recipients.find(r => r.id === recipientId);
  if (recipient) {
    if (field === 'phone') {
      originalValues[recipientId][field] = recipient.phone || '';
    } else if (field === 'name') {
      originalValues[recipientId][field] = recipient.name || '';
    } else if (field === 'email') {
      originalValues[recipientId][field] = recipient.email || '';
    } else {
      // Custom field
      originalValues[recipientId][field] = recipient.customFields?.[field] || '';
    }
  }
  
  editingStates[recipientId][field] = true;
};

const stopEditing = (recipientId, field) => {
  if (editingStates[recipientId]) {
    editingStates[recipientId][field] = false;
  }
};

const cancelEditing = (recipientId, field) => {
  stopEditing(recipientId, field);
  // Clear the stored original value
  if (originalValues[recipientId]) {
    delete originalValues[recipientId][field];
  }
};

const isEditing = (recipientId, field) => {
  return editingStates[recipientId]?.[field] || false;
};

// Grouped inbox options matching RecipientsListHeader with compatibility validation
const groupedInboxOptions = computed(() => {
  const result = [];
  const inboxes = props.inboxes || [];

  const whatsappEvolution = [];
  const whatsappOfficial = [];
  const messengerInboxes = [];
  const otherChannels = [];

  inboxes.forEach(inbox => {
    const name = (inbox.inbox_name || inbox.name || '').toLowerCase();

    // Check if this inbox is compatible with existing recipients
    const validation = validateInboxCompatibility(inbox.id);
    const inboxWithValidation = {
      ...inbox,
      disabled: !validation.valid,
      disabledReason: validation.error,
    };

    if (
      inbox.integration_type === 'whatsapp_cloud' ||
      inbox.integration_type === 'official' ||
      name.includes('api oficial') ||
      name.includes('cloud') ||
      name.includes('official')
    ) {
      whatsappOfficial.push({ ...inboxWithValidation, integration_type: 'official' });
    }
    else if (
      inbox.integration_type === 'evolution' ||
      name.includes('evolution') ||
      name.includes('unofficial')
    ) {
      whatsappEvolution.push({ ...inboxWithValidation, integration_type: 'evolution' });
    }
    else if (
      inbox.integration_type === 'messenger' ||
      inbox.integration_type === 'facebook_messenger' ||
      inbox.channel_type === 'Channel::FacebookPage' ||
      name.includes('messenger') ||
      name.includes('facebook')
    ) {
      messengerInboxes.push({ ...inboxWithValidation, integration_type: 'messenger' });
    }
    else {
      otherChannels.push(inboxWithValidation);
    }
  });

  if (whatsappEvolution.length > 0) {
    result.push({ type: 'header', label: 'WHATSAPP NÃO OFICIAL' });
    result.push(...whatsappEvolution);
  }

  if (whatsappOfficial.length > 0) {
    result.push({ type: 'header', label: 'WHATSAPP API OFICIAL' });
    result.push(...whatsappOfficial);
  }

  if (messengerInboxes.length > 0) {
    result.push({ type: 'header', label: 'FACEBOOK MESSENGER' });
    result.push(...messengerInboxes);
  }

  if (otherChannels.length > 0) {
    result.push({ type: 'header', label: 'OUTROS CANAIS' });
    result.push(...otherChannels);
  }

  return result;
});

// Determine the icon class with color based on inbox details
const getInboxIconWithColor = (inbox) => {
  if (!inbox) return 'i-lucide-message-square text-n-slate-11';

  const integrationType = inbox.integration_type;

  if (integrationType === 'official' || integrationType === 'whatsapp_cloud') {
    return 'i-ri-whatsapp-fill text-grass-600 dark:text-grass-500';
  }

  if (integrationType === 'evolution') {
    return 'i-lucide-message-circle text-grass-600 dark:text-grass-500';
  }

  if (integrationType === 'messenger' || integrationType === 'facebook_messenger') {
    return 'i-ri-messenger-fill text-n-brand dark:text-n-brand';
  }

  return 'i-lucide-message-square text-n-slate-11';
};

// Get inbox details by ID
const getInboxDetails = (inboxId) => {
  if (!inboxId) return null;
  return props.inboxes.find(inbox => inbox.id === inboxId);
};

// Helper: Get display name for inbox (matching RecipientsListHeader logic)
const getInboxDisplayName = (inbox) => {
  if (!inbox) return '-';

  // For Messenger, use display_name first, then profile (page name), then fallback
  if (inbox.integration_type === 'messenger' || inbox.integration_type === 'facebook_messenger') {
    return inbox.display_name || inbox.profile || inbox.inbox_name || inbox.name || '-';
  }

  // For other inboxes, use standard fallback
  return inbox.inbox_name || inbox.name || '-';
};

// Helper: Determine if an inbox is official, unofficial, messenger, or other
const getInboxType = (inboxId) => {
  const inbox = props.inboxes.find(i => i.id === inboxId);
  if (!inbox) return null;

  const name = (inbox.inbox_name || inbox.name || '').toLowerCase();

  // Check for Messenger first
  if (
    inbox.integration_type === 'messenger' ||
    inbox.integration_type === 'facebook_messenger' ||
    inbox.channel_type === 'Channel::FacebookPage' ||
    name.includes('messenger') ||
    name.includes('facebook')
  ) {
    return 'messenger';
  }

  // Check for Official WhatsApp
  if (
    inbox.integration_type === 'whatsapp_cloud' ||
    inbox.integration_type === 'official' ||
    inbox.provider === 'whatsapp_cloud_api'
  ) {
    return 'official';
  }

  // Check for Unofficial WhatsApp
  if (
    inbox.integration_type === 'evolution' ||
    (inbox.channel_type === 'whatsapp' && inbox.provider !== 'whatsapp_cloud_api')
  ) {
    return 'unofficial';
  }

  return 'other';
};

// Helper: Validate inbox compatibility with existing recipients
const validateInboxCompatibility = (newInboxId, existingRecipients = null) => {
  const recipients = existingRecipients || props.recipients;

  // If no recipients exist, any inbox is valid
  if (!recipients || recipients.length === 0) {
    return { valid: true, error: null, inboxType: getInboxType(newInboxId) };
  }

  const newInboxType = getInboxType(newInboxId);

  // Collect all existing inbox types from recipients
  const existingTypes = new Set();
  const existingOfficialInboxes = new Set();

  recipients.forEach(recipient => {
    const inboxId = recipient.selectedInbox || recipient.inbox_id;
    if (inboxId) {
      const type = getInboxType(inboxId);
      if (type) {
        existingTypes.add(type);
        if (type === 'official') {
          existingOfficialInboxes.add(inboxId);
        }
      }
    }
  });

  // Rule 1: Cannot mix WhatsApp (official or unofficial) with Messenger
  if (newInboxType === 'messenger' && (existingTypes.has('official') || existingTypes.has('unofficial'))) {
    return {
      valid: false,
      error: 'Não é possível misturar inboxes do Messenger com WhatsApp. Remova os contatos com WhatsApp primeiro.',
      inboxType: newInboxType
    };
  }

  if ((newInboxType === 'official' || newInboxType === 'unofficial') && existingTypes.has('messenger')) {
    return {
      valid: false,
      error: 'Não é possível misturar inboxes do WhatsApp com Messenger. Remova os contatos com Messenger primeiro.',
      inboxType: newInboxType
    };
  }

  // Rule 2: Cannot mix official with unofficial WhatsApp
  if (newInboxType === 'official' && existingTypes.has('unofficial')) {
    return {
      valid: false,
      error: 'Não é possível misturar API oficial com não oficial. Use apenas um tipo de WhatsApp.',
      inboxType: newInboxType
    };
  }

  if (newInboxType === 'unofficial' && existingTypes.has('official')) {
    return {
      valid: false,
      error: 'Não é possível misturar API não oficial com oficial. Use apenas um tipo de WhatsApp.',
      inboxType: newInboxType
    };
  }

  // Rule 3: Only 1 official WhatsApp inbox allowed
  if (newInboxType === 'official') {
    if (existingOfficialInboxes.size > 0 && !existingOfficialInboxes.has(newInboxId)) {
      return {
        valid: false,
        error: 'Apenas uma caixa de entrada oficial pode ser selecionada. Use a mesma inbox oficial para todos os contatos.',
        inboxType: newInboxType
      };
    }
  }

  // All rules passed
  return { valid: true, error: null, inboxType: newInboxType };
};

// Helper: Get the dominant inbox type from existing recipients
const getDominantInboxType = () => {
  if (!props.recipients || props.recipients.length === 0) return null;

  let officialCount = 0;
  let unofficialCount = 0;

  props.recipients.forEach(recipient => {
    const type = getInboxType(recipient.selectedInbox);
    if (type === 'official') officialCount++;
    else if (type === 'unofficial') unofficialCount++;
  });

  // Return the dominant type, or null if no clear type
  if (officialCount > 0 && unofficialCount === 0) return 'official';
  if (unofficialCount > 0 && officialCount === 0) return 'unofficial';
  return null; // Mixed or no clear type
};

// Helper: Find a compatible inbox for new contacts based on existing recipients
const findCompatibleInbox = (preferredInboxId = null) => {
  // If no existing recipients, use preferred or first available
  if (!props.recipients || props.recipients.length === 0) {
    if (preferredInboxId && props.inboxes.find(i => i.id === preferredInboxId)) {
      return preferredInboxId;
    }
    return props.inboxes.length > 0 ? props.inboxes[0].id : null;
  }

  // Get all existing inbox types
  const existingTypes = new Set();
  const existingOfficialInbox = props.recipients
    .map(r => r.selectedInbox || r.inbox_id)
    .filter(Boolean)
    .find(inboxId => getInboxType(inboxId) === 'official');

  props.recipients.forEach(recipient => {
    const inboxId = recipient.selectedInbox || recipient.inbox_id;
    if (inboxId) {
      const type = getInboxType(inboxId);
      if (type) {
        existingTypes.add(type);
      }
    }
  });

  // If preferred inbox is provided, check if it's compatible
  if (preferredInboxId) {
    const validation = validateInboxCompatibility(preferredInboxId);
    if (validation.valid) {
      return preferredInboxId;
    }
  }

  // Find first compatible inbox based on existing types
  for (const inbox of props.inboxes) {
    const validation = validateInboxCompatibility(inbox.id);
    if (validation.valid) {
      // For official type, if there's already an official inbox, use that one
      if (validation.inboxType === 'official' && existingOfficialInbox) {
        return existingOfficialInbox;
      }
      return inbox.id;
    }
  }

  // No compatible inbox found
  return null;
};

// Phone validation
const { countryOptions, validatePhoneNumber } = usePhoneValidation();
const selectedDDI = computed({
  get: () => props.selectedDDI,
  set: value => emit('update:selectedDDI', value),
});


// CSV import
const currentFile = ref(null);
const csvImport = ref(useCsvImport());

// Extract properties from current CSV import
const {
  parsedCsvRows,
  csvHeaders,
  showMapper,
  wizardStep,
  mapping,
  // guessColumnsMapping,  // Currently unused with commented handleImportCsv
  // processImportedData,  // Currently unused with commented handleImportCsv
  nextWizardStep,
  previousWizardStep,
  skipStep,
  cancelMapping,
} = csvImport.value;

// Additional properties for import processing
const isProcessing = computed(() => csvImport.value.isProcessing?.value || false);
const processingProgress = computed(() => csvImport.value.processingProgress?.value || 0);
const processingStage = computed(() => csvImport.value.processingStage?.value || '');
const processedRows = computed(() => csvImport.value.processedRows?.value || 0);
const totalRows = computed(() => csvImport.value.totalRows?.value || 0);

// File validation
const {
  validateFile,
  estimateProcessingTime,
  getPerformanceRecommendations,
  formatFileSize,
  clearValidation,
  LARGE_FILE_THRESHOLD
} = useFileValidation();

const showValidationModal = ref(false);
const validationResult = ref(null);
const estimatedTime = ref(null);
const performanceRecommendations = ref([]);

// File validation and processing helper
const handleFileValidationAndProcessing = async (file) => {
  if (!file) return;

  // Clear previous validation
  clearValidation();
  
  // Validate file
  const validation = validateFile(file);
  validationResult.value = validation;
  
  // Calculate estimated processing time
  estimatedTime.value = estimateProcessingTime(file.size);
  
  // Get performance recommendations
  performanceRecommendations.value = getPerformanceRecommendations(validation.fileSizeMB);

  // If there are errors or warnings, show validation modal
  if (!validation.isValid || validation.warnings.length > 0) {
    showValidationModal.value = true;
    return;
  }

  // If validation passes without warnings, proceed directly
  await proceedWithFileProcessing(file, validation);
};

const proceedWithFileProcessing = async (file, validation) => {
  // Store current file reference
  currentFile.value = file;
  
  // Process validation result
  
  try {
    // Use the CSV import method
    const currentImport = csvImport.value;
    await currentImport.handleFileUpload({ target: { files: [file] } });
  } catch (error) {
    console.error('Error processing file:', error);
    // You might want to show an error message to the user here
  }
};

const handleValidationModalContinue = async () => {
  showValidationModal.value = false;
  if (currentFile.value && validationResult.value) {
    await proceedWithFileProcessing(currentFile.value, validationResult.value);
  }
};

const handleValidationModalCancel = () => {
  showValidationModal.value = false;
  currentFile.value = null;
  validationResult.value = null;
  estimatedTime.value = null;
  performanceRecommendations.value = [];
  
  // Reset file input if it exists
  if (fileInput.value) {
    fileInput.value.value = '';
  }
};

// Recipients management
const {
  searchQuery,
  selectedRows,
  pagination,
  filteredRecipients,
  pagedRecipients,
  allSelected,
  handlePageChange,
  handlePageSizeChange,
  toggleAllSelection,
  toggleRowSelection,
  deleteSelectedRecipients,
  updateName,
  updatePhone: baseUpdatePhone,
  updateInbox,
  resetPagination,
} = useRecipients(props, emit);

// Random inbox distribution
const randomDistributionActive = ref(false);
const {
  showRandomizeModal,
  randomizableInboxes,
  totalPercentage,
  openRandomizeModal,
  closeRandomizeModal,
  balancePercentages,
  toggleRandomizableInbox,
  getInboxPercentage,
  selectAllInboxes,
  updateInboxPercentage,
  applyRandomInboxes,
} = useRandomInbox();

// Sistema de priorização de inboxes
const inboxPriority = {
  whatsapp_cloud: 1, // WhatsApp Oficial - Máxima prioridade
  evolution: 2, // Evolution - Segunda prioridade
  twilio_sms: 3, // SMS
  email: 4, // Email
  // outros tipos tem prioridade 999
};

// Computed property para detectar inbox oficial dominante
const dominantOfficialInbox = computed(() => {
  if (props.recipients.length === 0) return null;

  // Contar ocorrências de cada inbox oficial
  const inboxCount = {};
  let hasOfficialInbox = false;

  props.recipients.forEach(recipient => {
    const inbox = props.inboxes.find(i => i.id === recipient.selectedInbox);
    if (inbox && inbox.integration_type === 'whatsapp_cloud') {
      hasOfficialInbox = true;
      inboxCount[recipient.selectedInbox] =
        (inboxCount[recipient.selectedInbox] || 0) + 1;
    }
  });

  if (!hasOfficialInbox) return null;

  // Retornar a inbox oficial mais usada
  const mostUsedInbox = Object.entries(inboxCount).sort(
    ([, a], [, b]) => b - a
  )[0];

  return mostUsedInbox ? Number(mostUsedInbox[0]) : null;
});

// Text display
const {
  showFullTextModal,
  fullTextContent,
  fullTextTitle,
  openFullText,
  closeFullText,
} = useTextDisplay();

// Contacts selection modal state
const showSelectContactsModal = ref(false);
const globalInbox = ref(null);
const userManuallySelectedInbox = ref(false); // Flag para rastrear seleção manual de inbox

// Store applied mapping data to preserve column information after mapping is reset
const appliedMapping = ref(null);

// Computed properties
const inboxOptions = computed(() => {
  return props.inboxes.map(inbox => {
    const validation = validateInboxCompatibility(inbox.id);
    return {
      id: inbox.id,
      value: String(inbox.id),
      name: inbox.inbox_name || inbox.name,
      inbox_name: inbox.inbox_name,
      display_name: inbox.display_name,
      profile: inbox.profile,
      integration_type: inbox.integration_type,
      provider: inbox.provider,
      channel_type: inbox.channel_type,
      display_phone_number: inbox.display_phone_number,
      disabled: !validation.valid,
      disabledReason: validation.error,
    };
  });
});

const getInboxChannelType = inboxId => {
  if (!inboxId) return '';
  const inbox = props.inboxes.find(item => item.id === inboxId);
  if (!inbox) return '';

  const integrationType = String(inbox.integration_type || '').trim().toLowerCase();
  const channelType = String(inbox.channel_type || '').trim().toLowerCase();
  const provider = String(inbox.provider || '').trim().toLowerCase();

  if (
    integrationType === 'messenger' ||
    integrationType === 'facebook_messenger' ||
    channelType.includes('messenger') ||
    channelType.includes('facebookpage')
  ) {
    return 'messenger';
  }

  if (
    integrationType === 'official' ||
    integrationType === 'whatsapp_cloud' ||
    integrationType === 'evolution' ||
    provider === 'whatsapp_cloud_api' ||
    channelType.includes('whatsapp')
  ) {
    return 'whatsapp';
  }

  return '';
};

const getEffectiveInboxId = () => {
  if (globalInbox.value && globalInbox.value !== 'random') {
    const parsed = Number(globalInbox.value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  if (props.selectedInboxes && props.selectedInboxes.length > 0) {
    const parsed = Number(props.selectedInboxes[0]);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  const recipient = props.recipients.find(
    r => r.selectedInbox || r.inbox_id
  );
  if (recipient) {
    const parsed = Number(recipient.selectedInbox || recipient.inbox_id);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  return null;
};

const getInboxSelectionGroup = inboxId => {
  const channelGroup = getInboxChannelType(inboxId);
  if (channelGroup) return channelGroup;

  if (!inboxId) return '';
  const inbox = props.inboxes.find(item => item.id === inboxId);
  if (!inbox) return '';

  const integrationType = String(inbox.integration_type || '').trim().toLowerCase();
  const channelType = String(inbox.channel_type || '').trim().toLowerCase();

  if (integrationType) return `other:${integrationType}`;
  if (channelType) return `other:${channelType}`;
  return 'other';
};

const contactChannelFilter = computed(() => {
  return getInboxChannelType(getEffectiveInboxId());
});

// NEW: Filter to only show Evolution inboxes for random distribution
const evolutionInboxOptions = computed(() => {
  return inboxOptions.value.filter(
    inbox => inbox.integration_type === 'evolution'
  );
});

// NEW: Check if we have any evolution inboxes available
const hasEvolutionInboxes = computed(() => {
  return evolutionInboxOptions.value.length > 0;
});

const labelOptions = computed(() => {
  return props.availableLabels.map(label => ({
    id: label.id,
    name: label.title || label.name,
    value: label.id,
  }));
});

// Computed para mostrar o status de sincronização da inbox
const inboxSyncStatus = computed(() => {
  if (!globalInbox.value || globalInbox.value === 'random') {
    return {
      type: 'none',
      message: 'Nenhuma caixa selecionada',
      color: 'slate',
    };
  }

  const currentInbox = props.inboxes.find(
    i => i.id === Number(globalInbox.value)
  );
  if (!currentInbox)
    return { type: 'error', message: 'Caixa não encontrada', color: 'red' };

  if (currentInbox.integration_type === 'official') {
    return {
      type: 'official',
      message: 'Api Oficial',
      color: 'green',
      icon: 'i-lucide-shield-check',
    };
  }
  if (currentInbox.integration_type === 'evolution') {
    return {
      type: 'evolution',
      message: 'Api não oficial',
      color: 'yellow',
      icon: 'i-lucide-zap',
    };
  }

  return {
    type: 'other',
    message: `${currentInbox.name} sincronizado`,
    color: 'slate',
    icon: 'i-lucide-inbox',
  };
});

// Computed para verificar se há múltiplas inboxes
const hasMultipleInboxes = computed(() => {
  const uniqueInboxes = new Set(
    props.recipients.map(r => r.selectedInbox).filter(Boolean)
  );
  return uniqueInboxes.size > 1;
});


const selectedLabel = ref(null);
const onLabelChange = () => {
  if (selectedLabel.value) {
    emit('add-from-label', selectedLabel.value);
    selectedLabel.value = null;
  }
};

// Wrapper functions - updated to not use phone validation that adds "9"
const handleUpdatePhone = (id, value) => {
  // Pass the phone value directly without validation that adds "9"
  // The value should be stored exactly as the user types it
  emit('update-phone', id, value);
};

const handleUpdateEmail = (id, value) => {
  emit('update-email', id, value);
};

const handleAddManualContact = contactData => {
  emit('import-csv', [contactData]);
};

// MODIFIED: Modified to use only Evolution inboxes
const handleSelectAllInboxes = () =>
  selectAllInboxes(evolutionInboxOptions.value);

// Wrapper to provide necessary parameters to applyRandomInboxes
const handleApplyRandomInboxes = () => {
  const result = applyRandomInboxes(selectedRows.value, props.recipients, emit);
  if (result) {
    // Set the random distribution flag when successfully applied
    randomDistributionActive.value = true;
    // Clear the selected inbox to show "Aleatório" option
    globalInbox.value = 'random';
  }
};

// Contact modal methods
const openSelectContacts = () => {
  showSelectContactsModal.value = true;
};

const closeSelectContacts = () => {
  showSelectContactsModal.value = false;
};

const handleAddSelectedContacts = (newRecipients, selectedColumns = []) => {

  // Store the selected columns from CSV mapping for dynamic display
  if (selectedColumns && selectedColumns.length > 0) {
    // Update the applied mapping to include the additional columns for table display
    appliedMapping.value = {
      ...appliedMapping.value,
      additional_columns: [...selectedColumns],
    };
  }

  // Get suggested inbox from new recipients
  let suggestedInboxId = null;
  if (newRecipients.length > 0 && newRecipients[0].selectedInbox) {
    suggestedInboxId = newRecipients[0].selectedInbox;
  }

  // Determine compatible inbox based on existing recipients BEFORE emitting
  let compatibleInboxId = null;

  // PRIORITY 1: If user already selected a global inbox, use it if compatible
  if (globalInbox.value && globalInbox.value !== 'random') {
    compatibleInboxId = findCompatibleInbox(Number(globalInbox.value));
  }
  // PRIORITY 2: Try to use suggested inbox if it's compatible
  else if (suggestedInboxId) {
    compatibleInboxId = findCompatibleInbox(suggestedInboxId);
  }
  // PRIORITY 3: Find any compatible inbox
  else {
    compatibleInboxId = findCompatibleInbox();
  }

  // If no compatible inbox found, show error and abort
  if (!compatibleInboxId) {
    useAlert('Não foi possível encontrar uma inbox compatível. Remova os contatos existentes ou selecione contatos compatíveis.');
    return;
  }

  // Assign compatible inbox to all new recipients BEFORE sending to parent
  newRecipients.forEach(recipient => {
    recipient.selectedInbox = compatibleInboxId;
  });
  globalInbox.value = String(compatibleInboxId);

  // Send the data to the parent component with already-assigned inboxes
  emit('import-csv', newRecipients, selectedColumns);

  // Reset pagination to show newly added contacts
  resetPagination();
};

// Manually implement file upload handling
const handleFileUpload = event => {
  const file = event.target.files[0];
  if (!file) return;

  // Reset file input
  event.target.value = null;

  // Use the new file validation and processing function
  handleFileValidationAndProcessing(file);
};

// Note: handleImportCsv is currently not used directly in the template
// but may be useful for future direct CSV import functionality
// const handleImportCsv = csvData => {
//   // Handle the structured data from ContactSelectionModal
//   if (
//     csvData &&
//     typeof csvData === 'object' &&
//     csvData.data &&
//     csvData.headers
//   ) {
//     // Extract data from the structured format
//     parsedCsvRows.value = csvData.data;
//     csvHeaders.value = csvData.headers;

//     // Apply auto-detection of columns
//     guessColumnsMapping();

//     // Open mapping wizard
//     wizardStep.value = 0;
//     showMapper.value = true;

//     return;
//   }

//   // If this is a string (CSV data) or raw CSV data (legacy handling)
//   if (typeof csvData === 'string') {
//     Papa.parse(csvData, {
//       header: true,
//       skipEmptyLines: true,
//       complete: results => {
//         processImportedData(results.data, results.meta.fields);
//       },
//     });
//   } else if (csvData instanceof ArrayBuffer) {
//     // If it's an ArrayBuffer (Excel data), use SheetJS
//     try {
//       const data = new Uint8Array(csvData);
//       const workbook = XLSX.read(data, { type: 'array' });

//       // Get the first worksheet
//       const firstSheetName = workbook.SheetNames[0];
//       const worksheet = workbook.Sheets[firstSheetName];

//       // Convert worksheet to JSON with headers
//       const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

//       // Extract headers (first row) and data
//       const headers = jsonData[0];
//       const rows = jsonData.slice(1).map(row => {
//         // Create object with header keys
//         const rowData = {};
//         headers.forEach((header, index) => {
//           rowData[header] = row[index] || ''; // Handle undefined values
//         });
//         return rowData;
//       });

//       processImportedData(rows, headers);
//     } catch (error) {
//       console.error('Error processing Excel file:', error);
//     }
//   } else if (Array.isArray(csvData)) {
//     // If we already received processed data (array of objects)
//     // Just add it directly to the recipients
//     emit('import-csv', csvData);
//   }

//   // Close the contacts modal if it's open
//   closeSelectContacts();
// };

const handleBulkAction = action => {
  if (!selectedRows.value.length) return;

  if (action === 'delete') {
    deleteSelectedRecipients();
  } else if (action.startsWith('inbox-')) {
    const inboxId = parseInt(action.split('-')[1]);
    console.log('[RecipientList.handleBulkAction] Bulk inbox change:', {
      timestamp: new Date().toISOString(),
      action,
      inboxId,
      selectedRows: selectedRows.value.slice(0, 5), // Log first 5 for brevity
      selectedCount: selectedRows.value.length
    });
    emit('update-multiple-inboxes', selectedRows.value, inboxId);
  }
};

// Update all recipients to use the same inbox - simplified version
const updateAllRecipientsInbox = inboxId => {
  // When changing ALL recipients at once, we don't need to validate compatibility
  // because we're not mixing - we're replacing all inboxes with the same type
  // So we pass an empty array to indicate no existing recipients to validate against
  const validation = validateInboxCompatibility(inboxId, []);

  if (!validation.valid) {
    // Show error message to user
    useAlert(validation.error);
    return; // Prevent update
  }

  const effectiveInboxId = getEffectiveInboxId();
  const previousGroup = getInboxSelectionGroup(effectiveInboxId);
  const nextGroup = getInboxSelectionGroup(inboxId);

  console.log('[RecipientList] updateAllRecipientsInbox', {
    inboxId,
    effectiveInboxId,
    previousGroup,
    nextGroup,
    selectedRowsCount: selectedRows.value.length,
    globalInbox: globalInbox.value,
    selectedInboxes: props.selectedInboxes,
    recipientsCount: props.recipients.length,
  });

  const shouldClearRecipients =
    previousGroup && nextGroup && previousGroup !== nextGroup;

  if (shouldClearRecipients) {
    if (props.recipients.length > 0) {
      emit(
        'delete-selected',
        props.recipients.map(recipient => recipient.id)
      );
    }
    if (selectedRows.value.length > 0) {
      selectedRows.value = [];
    }
    console.log('[RecipientList] recipients cleared due to inbox group change');
  }

  // Update local UI state
  userManuallySelectedInbox.value = true;
  globalInbox.value = String(inboxId);
  randomDistributionActive.value = false; // Reset random distribution when manually selecting

  // Just emit to parent - let parent handle the actual update
  emit('set-all-recipients-inbox', inboxId);
};

// Modified applyMapping function for RecipientList.vue
const applyMapping = (payload = {}) => {

  // Create a set with NORMALIZED existing phones to avoid duplicates
  const existingPhones = new Set();

  // Normalize existing phone numbers to ensure consistent comparison
  props.recipients.forEach(r => {
    if (r.phone) {
      // Normalize by removing all non-digit characters
      const normalizedPhone = r.phone.replace(/\D/g, '');
      existingPhones.add(normalizedPhone);
    }
  });


  // Determine compatible inbox based on existing recipients
  let defaultInboxId = null;

  // PRIORITY 1: If user already selected a global inbox, find compatible one
  if (globalInbox.value && globalInbox.value !== 'random') {
    defaultInboxId = findCompatibleInbox(Number(globalInbox.value));
  }
  // PRIORITY 2: Find any compatible inbox
  else {
    defaultInboxId = findCompatibleInbox();
  }

  // If no compatible inbox found, show error and abort
  if (!defaultInboxId) {
    useAlert('Não foi possível encontrar uma inbox compatível para os contatos importados. Remova os contatos existentes ou importe contatos compatíveis.');
    return;
  }

  // Count duplicates for reporting
  let duplicateCount = 0;
  let invalidCount = 0;

  const mapped = parsedCsvRows.value
    .map((row, index) => {

      // Check if mapping.phone exists
      if (!mapping.phone) {
        console.warn('No phone mapping selected');
        return null;
      }

      // Try different ways to extract the phone value
      let phoneValue = null;

      // First try direct property access
      if (row[mapping.phone] !== undefined) {
        phoneValue = row[mapping.phone];
      }
      // Try other variations of the same property name
      else {
        // Try different casings and trimming
        const variations = [
          mapping.phone.trim(),
          mapping.phone.toLowerCase(),
          mapping.phone.toUpperCase(),
          // Also try with plural/singular variations
          mapping.phone.endsWith('s')
            ? mapping.phone.slice(0, -1)
            : mapping.phone + 's',
        ];

        for (const variation of variations) {
          if (row[variation] !== undefined) {
            phoneValue = row[variation];
            break;
          }
        }

        // If still not found, try to inspect all properties for any that might be phone-like
        if (phoneValue === null) {
          for (const key in row) {
            const value = row[key];
            // Check if this value looks like a phone number
            if (
              value &&
              typeof value === 'string' &&
              /\d{8,}/.test(value.replace(/\D/g, ''))
            ) {
              phoneValue = value;
              break;
            }
          }
        }
      }

      if (!phoneValue) {
        console.warn(
          `Row ${index} is missing value for selected phone column: ${mapping.phone}`
        );
        invalidCount++;
        return null;
      }

      // Improved phone normalization
      let normalizedPhone = String(phoneValue).replace(/\D/g, '');

      // Handle Brazilian numbers properly (remove 55 country code if present)
      if (normalizedPhone.startsWith('55') && normalizedPhone.length > 10) {
        // Check if the number starts with 55 (Brazil country code)
        // Only remove 55 if what follows looks like a complete Brazilian phone number
        if (normalizedPhone.length >= 12) {
          normalizedPhone = normalizedPhone.substring(2);
        }
      }

      // Add 9 prefix to 8-digit numbers if they have DDD (2 digits before)
      if (normalizedPhone.length === 10) {
        // This looks like a number with DDD but without the 9 prefix
        normalizedPhone =
          normalizedPhone.substring(0, 2) + '9' + normalizedPhone.substring(2);
      }


      // Check if this is a duplicate
      if (existingPhones.has(normalizedPhone)) {
        console.warn(`Duplicate phone: ${normalizedPhone}`);
        duplicateCount++;
        return null;
      }

      // Add to our set of existing phones
      existingPhones.add(normalizedPhone);

      // Create recipient object
      const recipient = {
        id: Date.now() + Math.random(),
        phone: normalizedPhone,
        name: '', // Will be set below if mapped
        email: '', // Will be set below if mapped
        selectedInbox: defaultInboxId,
      };

      // Helper function to get column value with variations
      const getColumnValue = (columnName, row) => {
        if (!columnName) return null;

        // First try direct access
        if (row[columnName] !== undefined) {
          return row[columnName];
        }

        // Try variations
        const variations = [
          columnName.trim(),
          columnName.toLowerCase(),
          columnName.toUpperCase(),
          columnName.endsWith('s') ? columnName.slice(0, -1) : columnName + 's',
        ];

        for (const variation of variations) {
          if (row[variation] !== undefined) {
            return row[variation];
          }
        }
        return null;
      };

      // Get name if available (legacy support)
      if (mapping.name) {
        const nameValue = getColumnValue(mapping.name, row);
        if (nameValue) {
          recipient.name = String(nameValue).trim();
        }
      }

      // Process additional columns
      if (
        mapping.additional_columns &&
        Array.isArray(mapping.additional_columns)
      ) {

        // Initialize customFields if we have additional columns
        if (!recipient.customFields) {
          recipient.customFields = {};
        }

        mapping.additional_columns.forEach(columnName => {
          const value = getColumnValue(columnName, row);

          const cleanValue = value ? String(value).trim() : '';
          const lowerColumnName = columnName.toLowerCase();

          // Map to appropriate recipient fields based on column name patterns
          if (
            lowerColumnName.includes('nome') ||
            lowerColumnName.includes('name')
          ) {
            if (cleanValue) {
              recipient.name = cleanValue;
            }
          } else if (
            lowerColumnName.includes('email') ||
            lowerColumnName.includes('e-mail')
          ) {
            if (cleanValue) {
              recipient.email = cleanValue;
            }
          } else {
            // For other columns, add as custom fields (even if empty, to ensure column appears)
            recipient.customFields[columnName] = cleanValue;
          }
        });
      }

      return recipient;
    })
    .filter(Boolean);


  // CRITICAL FIX: Make sure we actually have recipients to add
  if (mapped.length === 0) {
    console.warn('No valid recipients were mapped from the CSV');
    let errorMessage = 'Nenhum contato válido foi encontrado no arquivo.';

    if (duplicateCount > 0) {
      errorMessage += `\n${duplicateCount} contato(s) foram ignorados por serem duplicados.`;
    }

    if (invalidCount > 0) {
      errorMessage += `\n${invalidCount} contato(s) foram ignorados por terem números inválidos.`;
    }

    errorMessage +=
      '\nVerifique o formato dos números de telefone e tente novamente.';

    alert(errorMessage);
    return; // Don't close the modal so user can adjust mapping
  }

  // Update globalInbox if needed
  if (defaultInboxId && (!globalInbox.value || globalInbox.value === 'random')) {
    globalInbox.value = String(defaultInboxId);
  }

  // Emit the import event with the mapped recipients (inbox already assigned)
  emit('import-csv', mapped);

  // Reset pagination to ensure newly imported contacts are visible
  resetPagination();

  // Store the mapping data before closing modal (for potential future use)
  appliedMapping.value = {
    phone: mapping.phone,
    name: mapping.name,
    additional_columns: [...(mapping.additional_columns || [])],
  };

  // Close popup after import
  cancelMapping();

  // Close the contacts modal if open (unless skipContactModal is true)
  if (!payload.skipContactModal) {
    closeSelectContacts();
  }

};

// REMOVED: applyGlobalInbox function - no longer needed with simplified flow

// Helper function to determine the dominant inbox from recipients
const getDominantInboxFromRecipients = () => {
  if (!props.recipients || props.recipients.length === 0) return null;

  // Count inbox_id occurrences in recipients
  const inboxCounts = {};

  props.recipients.forEach(recipient => {
    // Priority: inbox_id (from retry data) > selectedInbox (UI selection)
    const inboxId = recipient.inbox_id || recipient.selectedInbox;
    if (inboxId) {
      inboxCounts[inboxId] = (inboxCounts[inboxId] || 0) + 1;
    }
  });


  // If no recipients have inbox assignments, return null
  if (Object.keys(inboxCounts).length === 0) return null;

  // Find the most common inbox_id
  const dominantInbox = Object.entries(inboxCounts).sort(
    ([, a], [, b]) => b - a
  )[0];

  const result = dominantInbox ? Number(dominantInbox[0]) : null;
  return result;
};

// Auto-select inbox based on recipients or first available inbox
onMounted(() => {
  // Add click outside listener
  document.addEventListener('click', handleClickOutside);
  // Wait a bit for props.inboxes to be populated
  setTimeout(() => {
    if (props.inboxes && props.inboxes.length > 0) {
      // First check if recipients already have inbox assignments
      const dominantInboxId = getDominantInboxFromRecipients();

      let selectedInboxId;
      if (
        dominantInboxId &&
        props.inboxes.find(inbox => inbox.id === dominantInboxId)
      ) {
        // Use the dominant inbox from recipients if it exists in available inboxes
        selectedInboxId = dominantInboxId;
      } else {
        // Fallback to first available inbox only if no recipients have inbox assignments
        selectedInboxId = props.inboxes[0].id;
      }

      globalInbox.value = String(selectedInboxId);

      // Automatically add this to selectedInboxes if there are recipients
      if (
        props.recipients.length > 0 &&
        !props.selectedInboxes.includes(selectedInboxId)
      ) {
        emit(
          'update-multiple-inboxes',
          props.recipients.map(r => r.id),
          selectedInboxId
        );
      }
    }
  }, 500);
});

// REMOVED: Watcher that was auto-applying dominant inbox and causing interference

// REMOVED: Watcher that was auto-selecting first inbox and causing interference

// REMOVED: Watcher on globalInbox that was causing redundant updates

// Sync globalInbox with selectedInboxes prop from parent (e.g., when InboxSelectionModal confirms)
watch(
  () => props.selectedInboxes,
  newSelectedInboxes => {
    if (newSelectedInboxes && newSelectedInboxes.length > 0) {
      const firstInboxId = newSelectedInboxes[0];
      // Only update if different to avoid loops
      if (globalInbox.value !== String(firstInboxId)) {
        globalInbox.value = String(firstInboxId);
        userManuallySelectedInbox.value = true;
      }
    }
  },
  { immediate: true }
);

watch(
  () => props.recipients.length,
  newLength => {
    // If all contacts are removed, reset the random distribution
    if (newLength === 0 && randomDistributionActive.value) {
      randomDistributionActive.value = false;
      if (globalInbox.value === 'random') {
        globalInbox.value = '';
      }
    }
  }
);

// Watch for changes in filtered data to keep pagination in bounds
watch(
  [() => filteredRecipients.value.length, () => pagination.value.pageSize],
  () => {
    const totalPages = Math.max(1, Math.ceil(filteredRecipients.value.length / pagination.value.pageSize));
    // pagination.value.pageIndex is 0-based; keep it within [0, totalPages-1]
    if (pagination.value.pageIndex > totalPages - 1) {
      pagination.value.pageIndex = Math.max(0, totalPages - 1);
    }
  },
  { immediate: true }
);

// Watcher para auto-sync com inbox oficial
watch(
  () => props.recipients.map(r => r.selectedInbox),
  () => {
    // Se não há recipients ou está em modo aleatório, não fazer nada
    if (props.recipients.length === 0 || randomDistributionActive.value) return;

    // Se o usuário selecionou manualmente uma inbox, não fazer auto-sync
    if (userManuallySelectedInbox.value) return;

    // Verificar se há inbox oficial
    const officialInboxId = dominantOfficialInbox.value;

    if (officialInboxId && globalInbox.value !== String(officialInboxId)) {
      // Auto-sincronizar com a inbox oficial
      globalInbox.value = String(officialInboxId);

      // Aplicar a inbox oficial usando bulk update para evitar condições de corrida
      const recipientsToUpdate = props.recipients
        .filter(recipient => recipient.selectedInbox !== officialInboxId)
        .map(recipient => recipient.id);
      
      if (recipientsToUpdate.length > 0) {
        emit('update-multiple-inboxes', recipientsToUpdate, officialInboxId);
      }
    }
  },
  { deep: true }
);

const handleUpdateMappingPhone = value => {
  mapping.phone = value;
};

const handleUpdateMappingName = value => {
  mapping.name = value;
};

const handleUpdateMappingAdditional = columns => {
  // Ensure we create a new array reference to trigger reactivity
  mapping.additional_columns = Array.isArray(columns) ? [...columns] : [];
};

const handleUpdateCustomField = (recipientId, fieldName, value) => {
  emit('update-custom-field', recipientId, fieldName, value);
};

// Handle click outside for closing inbox dropdowns
const handleClickOutside = (event) => {
  // Close all inbox editing states if clicking outside
  if (!event.target.closest('[data-inbox-dropdown], [data-inbox-dropdown-trigger]')) {
    Object.keys(editingStates).forEach(recipientId => {
      if (editingStates[recipientId]?.inbox) {
        editingStates[recipientId].inbox = false;
      }
    });
  }
};

// Special handler for inbox dropdown toggle
const toggleInboxDropdown = (recipientId, event) => {
  const buttonElement = event.currentTarget;
  emit('inbox-dropdown-toggle', recipientId, buttonElement);
};

// Auto-focus inputs when editing starts
const focusInput = (recipientId, field) => {
  setTimeout(() => {
    const input = document.querySelector(`[data-recipient="${recipientId}"][data-field="${field}"]`);
    if (input) {
      input.focus();
      input.select();
    }
  }, 10);
};

// Updated start editing function with focus
const startEditingWithFocus = (recipientId, field) => {
  startEditing(recipientId, field);
  focusInput(recipientId, field);
};

// Save changes on Enter
const saveChanges = (recipientId, field, value) => {
  if (field === 'phone') {
    handleUpdatePhone(recipientId, value);
    useAlert('Valor Telefone atualizado');
  } else if (field === 'name') {
    updateName(recipientId, value);
    useAlert('Valor Nome atualizado');
  } else if (field === 'email') {
    handleUpdateEmail(recipientId, value);
    useAlert('Valor Email atualizado');
  } else {
    // Custom field
    handleUpdateCustomField(recipientId, field, value);
    useAlert(`Valor ${field} atualizado`);
  }
  cancelEditing(recipientId, field);
};

// Cancel changes (restore original value)
const cancelChanges = (recipientId, field) => {
  cancelEditing(recipientId, field);
};

// Note: checkAndFixEmptyPage is no longer needed with the proper watcher
// The watcher automatically keeps pagination.value.pageIndex within valid bounds

// Handle key events
const handleKeyDown = (event, recipientId, field) => {
  if (event.key === 'Enter') {
    event.preventDefault();
    saveChanges(recipientId, field, event.target.value);
  } else if (event.key === 'Escape') {
    event.preventDefault();
    cancelChanges(recipientId, field);
  }
};

// Handle blur events (cancel changes)
const handleBlur = (recipientId, field) => {
  cancelChanges(recipientId, field);
};
</script>

<template>
  <div class="space-y-5">
    <!-- Main header with controls -->
    <RecipientsListHeader
      :inbox-options="inboxOptions"
      :global-inbox="globalInbox"
      :random-distribution-active="randomDistributionActive"
      :selected-d-d-i="selectedDDI"
      :country-options="countryOptions"
      :recipients-count="props.recipients.length"
      :recipients="props.recipients"
      :search-query="searchQuery"
      :selected-rows="selectedRows"
      :inbox-sync-status="inboxSyncStatus"
      :has-multiple-inboxes="hasMultipleInboxes"
      @open-select-contacts="openSelectContacts"
      @update:global-inbox="globalInbox = $event"
      @update:selected-d-d-i="selectedDDI = $event"
      @open-randomize-modal="openRandomizeModal"
      @update:search-query="searchQuery = $event"
      @delete-selected="deleteSelectedRecipients"
      @update-bulk-action="handleBulkAction"
      @update-all-recipients-inbox="updateAllRecipientsInbox"
    />

    <!-- Note: The inbox type warning modal is handled in RecipientsTable.vue -->

    <!-- Modal for Random Distribution -->
    <div
      v-if="showRandomizeModal"
      class="fixed inset-0 z-50 flex items-center justify-center"
      style="
        background-color: rgba(0, 0, 0, 0.04);
        backdrop-filter: blur(4px);
        min-height: 100vh;
        width: 100vw;
        margin: 0;
        top: 0;
        left: 0;
      "
    >
      <div
        class="relative max-h-[95vh] overflow-auto bg-n-alpha-3 dark:bg-n-slate-2 shadow-md modal-container rtl:text-right skip-context-menu rounded-xl w-full max-w-lg mx-auto"
      >
        <div
          class="flex justify-between items-center px-6 py-2 border-n-slate-4 dark:border-n-slate-7"
        >
          <h3 class="text-lg font-semibold text-n-slate-12">
            Distribuição de Caixas de Entrada
          </h3>
          <button
            class="text-n-slate-11 hover:text-n-slate-12"
            @click="closeRandomizeModal"
          >
            <i class="i-lucide-x text-xl" />
          </button>
        </div>

        <div class="px-6 py-4">
          <!-- NEW: Warning message about only being able to use unofficial inboxes -->
          <div class="bg-n-amber-1 dark:bg-n-amber-3/10 border border-n-amber-3 dark:border-n-amber-7 rounded-lg p-3 mb-4">
            <div class="flex items-center">
              <i class="i-lucide-alert-triangle text-n-amber-11 dark:text-n-amber-9 mr-2" />
              <p class="text-sm text-n-amber-11 dark:text-n-amber-9">
                A distribuição aleatória só é possível com caixas de entrada
                <strong>não oficiais</strong>.
              </p>
            </div>
          </div>

          <p class="text-sm text-n-slate-11 dark:text-n-slate-9 mb-4">
            Selecione as caixas de entrada e defina a porcentagem de
            distribuição para cada uma.
          </p>

          <!-- No Evolution inboxes message -->
          <div
            v-if="!hasEvolutionInboxes"
            class="bg-n-slate-1 dark:bg-n-slate-2 rounded-lg border border-n-slate-3 dark:border-n-slate-6 p-6 text-center"
          >
            <i class="i-lucide-inbox text-4xl text-n-slate-9 dark:text-n-slate-7 mb-2" />
            <p class="text-n-slate-11 dark:text-n-slate-9">
              Nenhuma caixa de entrada disponível.
            </p>
          </div>

          <!-- Inboxes Selection with Percentage -->
          <div
            v-else
            class="bg-n-slate-1 dark:bg-n-slate-2 rounded-lg border border-n-slate-3 dark:border-n-slate-6 p-4 max-h-[300px] overflow-y-auto"
          >
            <div
              v-for="inbox in evolutionInboxOptions"
              :key="inbox.id"
              class="flex items-center mb-4 last:mb-0 pb-3 border-b border-n-slate-3 dark:border-n-slate-6 last:border-0 last:pb-0"
              :class="[
                randomizableInboxes.includes(Number(inbox.value))
                  ? ''
                  : 'opacity-60',
              ]"
            >
              <div class="flex items-center flex-1">
                <Checkbox
                  :model-value="
                    randomizableInboxes.includes(Number(inbox.value))
                  "
                  @update:model-value="
                    () => toggleRandomizableInbox(Number(inbox.value))
                  "
                />
                <label
                  class="ml-2 text-n-slate-12 dark:text-n-slate-1 cursor-pointer flex-1"
                  @click="toggleRandomizableInbox(Number(inbox.value))"
                >
                  {{ inbox.inbox_name || inbox.name }}
                </label>
              </div>

              <!-- Percentage Input -->
              <div class="flex items-center ml-2">
                <input
                  type="number"
                  min="1"
                  max="100"
                  class="w-16 px-2 py-1 border border-n-slate-4 dark:border-n-slate-7 rounded text-right bg-n-solid-1 dark:bg-n-slate-3 text-n-slate-12 dark:text-n-slate-1"
                  :class="{
                    'opacity-60 cursor-not-allowed':
                      !randomizableInboxes.includes(Number(inbox.value)),
                  }"
                  :disabled="!randomizableInboxes.includes(Number(inbox.value))"
                  :value="getInboxPercentage(Number(inbox.value))"
                  @input="
                    e =>
                      updateInboxPercentage(
                        Number(inbox.value),
                        parseInt(e.target.value) || 0
                      )
                  "
                />
                <span class="ml-1 text-n-slate-11 dark:text-n-slate-9">%</span>
              </div>
            </div>
          </div>

          <!-- Percentage Total Bar -->
          <div class="mt-4">
            <div class="flex justify-between items-center mb-1">
              <span class="text-xs text-n-slate-11 dark:text-n-slate-9">Distribuição Total</span>
              <span
                class="text-xs font-medium"
                :class="
                  totalPercentage === 100
                    ? 'text-n-green-11'
                    : 'text-n-amber-11'
                "
              >
                {{ totalPercentage }}%
              </span>
            </div>

            <div class="h-2 bg-n-slate-3 dark:bg-n-slate-7 rounded-full overflow-hidden">
              <div
                class="h-full transition-all duration-300"
                :class="
                  totalPercentage === 100 ? 'bg-n-green-9' : 'bg-n-amber-9'
                "
                :style="`width: ${Math.min(100, totalPercentage)}%`"
              />
            </div>
          </div>

          <!-- Selection Info -->
          <div
            v-if="hasEvolutionInboxes"
            class="mt-4 flex items-center justify-between"
          >
            <div class="text-sm text-n-slate-11 dark:text-n-slate-9">
              {{ randomizableInboxes.length }} de
              {{ evolutionInboxOptions.length }} caixas selecionadas
            </div>

            <div class="flex gap-2">
              <Button
                variant="outline"
                color="slate"
                size="sm"
                label="Selecionar todas"
                :disabled="
                  randomizableInboxes.length === evolutionInboxOptions.length
                "
                @click="handleSelectAllInboxes"
              />
              <Button
                variant="outline"
                color="slate"
                size="sm"
                label="Limpar"
                :disabled="randomizableInboxes.length === 0"
                @click="randomizableInboxes = []"
              />
            </div>
          </div>
        </div>

        <div class="px-6 py-4 border-t border-n-slate-3 dark:border-n-slate-6 flex justify-between">
          <Button
            label="Cancelar"
            variant="outline"
            color="slate"
            @click="closeRandomizeModal"
          />
          <Button
            label="Aplicar Distribuição"
            color="blue"
            :disabled="
              randomizableInboxes.length === 0 ||
              totalPercentage !== 100 ||
              !hasEvolutionInboxes
            "
            @click="handleApplyRandomInboxes"
          />
        </div>
      </div>
    </div>

    <!-- Recipients Table -->
    <div class="shadow outline-1 outline outline-n-container dark:outline-none rounded-xl bg-n-solid-1 dark:bg-n-slate-2 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-n-slate-4">
          <thead class="bg-n-solid-1">
            <tr>
              <!-- Checkbox column -->
              <th class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 dark:text-n-slate-11 uppercase tracking-wider w-16">
                <Checkbox
                  :model-value="allSelected"
                  @update:model-value="toggleAllSelection"
                />
              </th>
              
              <!-- Phone column (moved first) -->
              <th class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 dark:text-n-slate-11 uppercase tracking-wider">
                Telefone
              </th>
              
              <!-- Name column -->
              <th class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 dark:text-n-slate-11 uppercase tracking-wider">
                Nome
              </th>
              
              <!-- Email column -->
              <th class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 dark:text-n-slate-11 uppercase tracking-wider">
                Email
              </th>
              
              <!-- Dynamic custom field columns -->
              <th
                v-for="column in (appliedMapping?.additional_columns || props.additionalColumns || mapping.additional_columns || [])"
                :key="column"
                class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 dark:text-n-slate-11 uppercase tracking-wider"
              >
                {{ column }}
              </th>
              
              <!-- Inbox column -->
              <th class="px-3 py-3 text-left text-xs font-medium text-n-slate-11 dark:text-n-slate-11 uppercase tracking-wider">
                Inboxes
              </th>
              
              <!-- Actions column -->
              <th class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 dark:text-n-slate-11 uppercase tracking-wider w-24">
                Ações
              </th>
            </tr>
          </thead>
          
          <tbody class="bg-n-solid-1 dark:bg-n-slate-1 divide-y divide-n-slate-4 dark:divide-y-0">
            <tr
              v-for="recipient in pagedRecipients"
              :key="recipient.id"
              :class="{
                'bg-n-woot-50 dark:bg-n-woot-800/20': selectedRows.includes(recipient.id)
              }"
            >
              <!-- Checkbox column -->
              <td class="px-6 py-4 whitespace-nowrap">
                <Checkbox
                  :model-value="selectedRows.includes(recipient.id)"
                  @update:model-value="() => toggleRowSelection(recipient.id)"
                />
              </td>
              
              <!-- Phone column (editable, moved first) -->
              <td class="px-6 py-4 whitespace-nowrap relative">
                <div
                  v-if="!isEditing(recipient.id, 'phone')"
                  v-tooltip.top="recipient.phone && !recipient.phone.startsWith('55') && !recipient.phone.startsWith('+55') ? 'Código de país não especificado' : ''"
                  class="min-h-[24px] text-base text-n-slate-12 dark:text-n-slate-11 hover:bg-n-slate-2 dark:hover:bg-n-slate-6 rounded cursor-text transition-colors"
                  :class="{
                    'text-ruby-700 dark:text-ruby-600': validationErrors.has(recipient.id) || (recipient.phone && !recipient.phone.startsWith('55') && !recipient.phone.startsWith('+55')),
                    'text-n-slate-9 dark:text-n-slate-10': !recipient.phone
                  }"
                  @click="startEditingWithFocus(recipient.id, 'phone')"
                >
                  {{ recipient.phone || '-' }}
                </div>
                <input
                  v-else
                  type="text"
                  :value="recipient.phone || ''"
                  :data-recipient="recipient.id"
                  data-field="phone"
                  class="w-full px-2 py-1 text-base border border-n-slate-6 dark:border-n-slate-7 rounded focus:ring-2 focus:ring-n-brand focus:border-n-brand bg-n-solid-1 dark:bg-n-slate-2 text-n-slate-12 dark:text-n-slate-11"
                  :class="{
                    'border-ruby-700 focus:ring-ruby-700 focus:border-ruby-700': validationErrors.has(recipient.id)
                  }"
                  placeholder="Telefone"
                  @blur="handleBlur(recipient.id, 'phone')"
                  @keydown="handleKeyDown($event, recipient.id, 'phone')"
                />
              </td>
              
              <!-- Name column (editable) -->
              <td class="px-6 py-4 whitespace-nowrap relative">
                <div
                  v-if="!isEditing(recipient.id, 'name')"
                  class="min-h-[24px] text-base text-n-slate-12 dark:text-n-slate-11 hover:bg-n-slate-2 dark:hover:bg-n-slate-6 rounded cursor-text transition-colors"
                  :class="{ 'text-n-slate-9 dark:text-n-slate-10': !recipient.name }"
                  @click="startEditingWithFocus(recipient.id, 'name')"
                >
                  {{ recipient.name || '-' }}
                </div>
                <input
                  v-else
                  type="text"
                  :value="recipient.name || ''"
                  :data-recipient="recipient.id"
                  data-field="name"
                  class="w-full text-base border border-n-slate-6 dark:border-n-slate-7 rounded focus:ring-2 focus:ring-n-brand focus:border-n-brand bg-n-solid-1 dark:bg-n-slate-2 text-n-slate-12 dark:text-n-slate-11"
                  placeholder="Nome"
                  @blur="handleBlur(recipient.id, 'name')"
                  @keydown="handleKeyDown($event, recipient.id, 'name')"
                />
              </td>
              
              <!-- Email column (editable) -->
              <td class="px-6 py-4 whitespace-nowrap relative">
                <div
                  v-if="!isEditing(recipient.id, 'email')"
                  class="min-h-[24px] text-base text-n-slate-12 dark:text-n-slate-11 hover:bg-n-slate-2 dark:hover:bg-n-slate-6 rounded cursor-text transition-colors"
                  :class="{ 'text-n-slate-9 dark:text-n-slate-10': !recipient.email }"
                  @click="startEditingWithFocus(recipient.id, 'email')"
                >
                  {{ recipient.email || '-' }}
                </div>
                <input
                  v-else
                  type="email"
                  :value="recipient.email || ''"
                  :data-recipient="recipient.id"
                  data-field="email"
                  class="w-full px-2 py-1 text-base border border-n-slate-6 dark:border-n-slate-7 rounded focus:ring-2 focus:ring-n-brand focus:border-n-brand bg-n-solid-1 dark:bg-n-slate-2 text-n-slate-12 dark:text-n-slate-11"
                  placeholder="Email"
                  @blur="handleBlur(recipient.id, 'email')"
                  @keydown="handleKeyDown($event, recipient.id, 'email')"
                />
              </td>
              
              <!-- Dynamic custom field columns -->
              <td
                v-for="column in (appliedMapping?.additional_columns || props.additionalColumns || mapping.additional_columns || [])"
                :key="column"
                class="px-6 py-4 whitespace-nowrap relative"
              >
                <div
                  v-if="!isEditing(recipient.id, column)"
                  class="min-h-[24px] text-base text-n-slate-12 dark:text-n-slate-11 hover:bg-n-slate-2 dark:hover:bg-n-slate-6 rounded cursor-text transition-colors"
                  :class="{ 'text-n-slate-9 dark:text-n-slate-10': !recipient.customFields?.[column] }"
                  @click="startEditingWithFocus(recipient.id, column)"
                >
                  {{ recipient.customFields?.[column] || '-' }}
                </div>
                <input
                  v-else
                  type="text"
                  :value="recipient.customFields?.[column] || ''"
                  :data-recipient="recipient.id"
                  :data-field="column"
                  class="w-full px-2 py-1 text-base border border-n-slate-6 dark:border-n-slate-7 rounded focus:ring-2 focus:ring-n-brand focus:border-n-brand bg-n-solid-1 dark:bg-n-slate-2 text-n-slate-12 dark:text-n-slate-11"
                  :placeholder="column"
                  @blur="handleBlur(recipient.id, column)"
                  @keydown="handleKeyDown($event, recipient.id, column)"
                />
              </td>
              
              <!-- Inbox column (dropdown for unofficial, static for official/messenger) -->
              <td class="px-3 py-4 whitespace-nowrap relative">
                <!-- Static Display (for official and messenger inboxes - non-interactive) -->
                <div
                  v-if="getInboxType(recipient.selectedInbox) === 'official' || getInboxType(recipient.selectedInbox) === 'messenger'"
                  class="min-h-[32px] text-n-slate-12 dark:text-n-slate-11 flex items-center min-w-[100px]"
                >
                  <div class="flex items-center overflow-hidden">
                    <!-- Icon without background -->
                    <i
                      v-if="getInboxDetails(recipient.selectedInbox)"
                      :class="getInboxIconWithColor(getInboxDetails(recipient.selectedInbox)) + ' text-xl mr-2 flex-shrink-0'"
                    />
                    <span class="truncate text-base">
                      {{ getInboxDisplayName(getInboxDetails(recipient.selectedInbox)) }}
                    </span>
                  </div>
                </div>

                <!-- Dropdown Button (for unofficial and other inboxes - interactive) -->
                <div
                  v-else
                  class="min-h-[32px] px-3 border border-n-slate-6 dark:border-n-slate-7 rounded-lg bg-n-solid-1 dark:bg-n-slate-3 text-n-slate-12 dark:text-n-slate-11 hover:bg-n-slate-1 dark:hover:bg-n-slate-4 focus:border-n-brand focus:outline-none flex justify-between items-center min-w-[100px] transition-all duration-200 cursor-pointer"
                  :data-recipient="recipient.id"
                  data-inbox-dropdown-trigger
                  @click.stop="toggleInboxDropdown(recipient.id, $event)"
                >
                  <div class="flex items-center overflow-hidden">
                    <!-- Icon without background -->
                    <i
                      v-if="getInboxDetails(recipient.selectedInbox)"
                      :class="getInboxIconWithColor(getInboxDetails(recipient.selectedInbox)) + ' text-xl mr-2 flex-shrink-0'"
                    />
                    <span class="truncate text-base">
                      {{ getInboxDisplayName(getInboxDetails(recipient.selectedInbox)) }}
                    </span>
                  </div>
                  <i
                    class="ml-2 flex-shrink-0 transition-transform duration-200 i-lucide-chevron-down"
                  />
                </div>
              </td>
              
              <!-- Actions column -->
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <button
                  class="text-n-ruby-11 hover:text-n-ruby-12 dark:text-n-ruby-10 dark:hover:text-n-ruby-11 transition-colors"
                  @click="emit('remove', recipient.id)"
                >
                  <i class="i-lucide-trash-2 text-lg" />
                </button>
              </td>
            </tr>
            
            <!-- Empty state -->
            <tr v-if="pagedRecipients.length === 0">
              <td :colspan="6 + (appliedMapping?.additional_columns?.length || props.additionalColumns?.length || mapping.additional_columns?.length || 0)" class="px-6 py-16 text-center text-n-slate-11 dark:text-n-slate-9">
                <!-- Show different icon and message based on search state -->
                <i v-if="searchQuery" class="i-lucide-search text-n-slate-8 dark:text-n-slate-6 w-20 h-20 mb-2" />
                <i v-else class="i-lucide-users text-n-slate-8 dark:text-n-slate-6 w-20 h-20 mb-2" />
                <p class="text-base text-n-slate-11 dark:text-n-slate-9">
                  {{ searchQuery ? 'Nenhum contato encontrado' : 'Nenhum contato selecionado' }}
                </p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- Pagination -->
      <div v-show="filteredRecipients.length > 0" class="bg-n-solid-1 px-6 py-3 border-t border-n-slate-4">
        <ContactsPagination
          :current-page="pagination.pageIndex + 1"
          :total-pages="Math.max(1, Math.ceil(filteredRecipients.length / pagination.pageSize))"
          :page-size="pagination.pageSize"
          :page-size-options="[10, 25, 50, 100, 500, 1000]"
          :show-page-size-selector="true"
          :current-items-count="pagedRecipients.length"
          :total-items-count="filteredRecipients.length"
          item-name="contato"
          @page-change="handlePageChange"
          @page-size-change="handlePageSizeChange"
        />
      </div>
    </div>

    <!-- Hidden file input for CSV upload -->
    <input
      ref="fileInput"
      type="file"
      accept=".csv,.xlsx,.xls"
      class="hidden"
      @change="handleFileUpload"
    />

    <!-- Contacts Selection Modal -->
    <AddContactsModal
      :show="showSelectContactsModal"
      :chatwoot-contacts="props.chatwootContacts"
      :is-loading-contacts="props.isLoadingContacts"
      :total-contacts-count="props.totalContactsCount"
      :current-contacts-page="props.currentContactsPage"
      :total-contacts-pages="props.totalContactsPages"
      :available-labels="props.availableLabels"
      :existing-recipients="props.recipients"
      :inboxes="props.inboxes"
      :channel-type-filter="contactChannelFilter"
      :selected-inbox-id="props.selectedInboxId"
      @close="closeSelectContacts"
      @load-contacts-page="$emit('load-contacts-page', $event)"
      @add-contacts="handleAddSelectedContacts"
    />

    <!-- CSV Mapping Modal -->
    <CsvMappingModal
      :show="showMapper"
      :wizard-step="wizardStep"
      :mapping="mapping"
      :parsed-csv-rows="parsedCsvRows"
      :csv-headers="csvHeaders"
      @update:mapping-phone="handleUpdateMappingPhone"
      @update:mapping-name="handleUpdateMappingName"
      @update:mapping-additional="handleUpdateMappingAdditional"
      @next-step="nextWizardStep"
      @previous-step="previousWizardStep"
      @skip-step="skipStep"
      @apply-mapping="applyMapping"
      @cancel="cancelMapping"
    />

    <!-- CSV Processing Progress Overlay -->
    <CsvProcessingProgress
      v-if="isProcessing"
      :progress="processingProgress"
      :stage="processingStage"
      :processed-rows="processedRows"
      :total-rows="totalRows"
    />

    <!-- Full Text Modal -->
    <FullTextModal
      :show="showFullTextModal"
      :title="fullTextTitle"
      :content="fullTextContent"
      @close="closeFullText"
    />

    <!-- File Validation Modal -->
    <FileValidationModal
      :show="showValidationModal"
      :validation-result="validationResult"
      :estimated-time="estimatedTime"
      :performance-recommendations="performanceRecommendations"
      @continue="handleValidationModalContinue"
      @cancel="handleValidationModalCancel"
    />
  </div>
</template>

<style scoped>
/* Add this new animation for the info icon */
@keyframes gentle-pulse {
  0% {
    opacity: 0.7;
    transform: scale(1);
  }

  50% {
    opacity: 1;
    transform: scale(1.1);
  }

  100% {
    opacity: 0.7;
    transform: scale(1);
  }
}

.animate-pulse {
  animation: gentle-pulse 2s infinite;
}
</style>
